import { useQuery } from '@tanstack/react-query'
import { apiClient, getSupabase } from '@dogdisk/shared'

/** paid 包最低价提示(UI 文案用;权威值在后端 pack.paid_min_price_coin) */
export const PAID_MIN_HINT = 10

export type Pack = {
  id: string
  integrator_id: string
  name: string
  description: string | null
  access_mode: 'free' | 'paid'
  price_coin: number | null
  password: string | null
  expires_at: string | null
  allow_save_to_pan: boolean
  allow_download: boolean
  status: 'active' | 'cancelled' | 'expired'
  created_at: string
  view_count?: number
  subscriber_count?: number
  cover_thumb_url?: string | null
  cover_file_info_id?: string | null
}

export type FileInfoOption = {
  id: string
  name: string
  type: string
  size: number | null
  mime_type: string | null
  base_file_id: string | null
  thumb_url_list?: string | null // 后端签名缩略图计算列;空 → 回退原图直链
  created_at?: string | null // 2026-06-07 UGuBc 行 meta 显示用
}

export function useMyPacks() {
  return useQuery({
    queryKey: ['pack', 'mine'] as const,
    queryFn: async () => {
      const sb = getSupabase()
      // owner 收口走 v_resource_pack_mine(内建 integrator_id=auth.uid())。直查 resource_pack
      // 会因 pack_select_public policy 漏入别人的公开包(同 file_info 越权同类)。
      const { data, error } = await sb
        .from('v_resource_pack_mine')
        .select(
          'id, integrator_id, name, description, access_mode, price_coin, password, expires_at, allow_save_to_pan, allow_download, status, created_at, subscriber_count, view_count, cover_thumb_url, cover_file_info_id',
        )
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as Pack[]
    },
  })
}

// 2026-06-07 UGuBc:从 useFilePickList 提出独立函数 — 添加文件页的文件夹递归展开
// (pack-file-expand)需要在 react-query 之外按 parentId 逐层拉页。

/** v_file_info_mine 行映射 → FileInfoOption(fetchFilePickPage + 预选查询复用) */
export type FileInfoMineRow = {
  id: string
  name: string
  type: string
  base_file_id: string | null
  thumb_url_list: string | null
  size_original: number | null
  mime_type: string | null
  created_at: string | null
}

export function fileInfoOptionFromMine(r: FileInfoMineRow): FileInfoOption {
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    base_file_id: r.base_file_id ?? null,
    thumb_url_list: r.thumb_url_list ?? null,
    size: r.size_original ?? null,
    mime_type: r.mime_type ?? null,
    created_at: r.created_at ?? null,
  }
}

export async function fetchFilePickPage(parentId: string | null): Promise<FileInfoOption[]> {
  const sb = getSupabase()
  // owner 收口走 v_file_info_mine(内建 user_id=auth.uid() + status='active');size/mime/thumb 已平坦化。
  // 资源包只能挑自己的文件:直查 file_info 会因 file_info_select_visibility 宽 policy
  //(is_public 默认 true)漏入别人的公开文件,把别人的文件混进选择器。
  let q = sb
    .from('v_file_info_mine')
    .select('id, name, type, base_file_id, thumb_url_list, size_original, mime_type, created_at')
    .order('type', { ascending: false })
    .order('name', { ascending: true })
  q = parentId === null ? q.is('parent_id', null) : q.eq('parent_id', parentId)
  const { data, error } = await q
  if (error) throw error
  return ((data ?? []) as unknown as FileInfoMineRow[]).map(fileInfoOptionFromMine)
}

export function useFilePickList(parentId: string | null) {
  return useQuery({
    queryKey: ['file', 'pick', parentId] as const,
    queryFn: () => fetchFilePickPage(parentId),
  })
}

export type CreatePackInput = {
  access_mode: 'free' | 'paid'
  name: string
  description?: string | undefined
  // 封面:create 端点直收(后端 validateCover 归属/active/image/≤5MB);
  // resource_pack.cover_file_info_id 列级直写通道已 REVOKE(20260612000200)
  cover_file_info_id?: string | undefined
  price_coin?: number | null | undefined
  password?: string | null | undefined
  expires_at?: string | null | undefined
  allow_save_to_pan: boolean
  allow_download: boolean
  items: Array<{ file_info_id: string; position: number }>
}

export type CreatePackResp = {
  pack_id: string
  share_url_path: string
  created_at: string
}

export async function createPack(input: CreatePackInput): Promise<CreatePackResp> {
  return apiClient.post<CreatePackResp>('/gapi/user/packs', input)
}

// ---- KkJt4 资源包设置(2026-06-11) ----

export type PackDetailItem = {
  file_info_id: string
  name: string
  type: string
  size_original: number | null
  mime_type: string | null
  position: number
  cover_thumb_url?: string | null
}

export type PackDetail = {
  pack_id: string
  // 包内容根 pack_folder(子树模型);管理内容 = 跳 /folders/{folder_file_info_id}
  folder_file_info_id: string | null
  name: string
  description: string | null
  access_mode: 'free' | 'paid'
  price_coin: number | null
  status: string
  cover_file_info_id: string | null
  cover_thumb_url: string | null
  items: PackDetailItem[]
}

export function usePackDetail(packId: string | undefined) {
  return useQuery({
    queryKey: ['pack', 'detail', packId] as const,
    enabled: Boolean(packId),
    queryFn: () => apiClient.get<PackDetail>(`/gapi/user/packs/${encodeURIComponent(packId!)}`),
  })
}

/** GetMyPackDetail item → 添加文件页 FileInfoOption(base_file/thumb/created_at detail 不带,置 null) */
export function itemToFileInfoOption(it: PackDetailItem): FileInfoOption {
  return {
    id: it.file_info_id,
    name: it.name,
    type: it.type,
    size: it.size_original ?? null,
    mime_type: it.mime_type ?? null,
    base_file_id: null,
    thumb_url_list: null,
    created_at: null,
  }
}

export type UpdatePackInput = {
  name?: string
  description?: string
  price_coin?: number
  cover_file_info_id?: string
  clear_cover?: boolean
  // 子树模型(2026-06-13):包内容 = pack_folder 真实 file_info 子树,由文件夹页(/folders/{id})
  // 直接增删改管理;元数据面板(KkJt4)不再传 items 整体替换。字段保留兼容(建包/旧调用),
  // 不传 = 不动内容(后端 nil 语义)。
  items?: Array<{ file_info_id: string; position: number }>
}

/** PATCH /gapi/user/packs/{id} */
export async function updatePack(packId: string, input: UpdatePackInput): Promise<void> {
  await apiClient.patch(`/gapi/user/packs/${encodeURIComponent(packId)}`, input)
}

// ---- 我的订阅(2026-06-12 xwUX2 订阅 tab) ----

export type SubscriptionRow = {
  pack_id: string
  subscribed_at: string
  name: string
  description: string | null
  price_coin: number | null
  status: string
  integrator_id: string
  cover_thumb_url: string | null
}

/** 列表卡片公共形状(订阅/分享两 tab 复用 PackCoverThumb 卡片) */
export type PackCard = {
  id: string
  name: string
  coverThumbUrl: string | null
  sortKey: string
}

export function subscriptionToCard(r: SubscriptionRow): PackCard {
  return { id: r.pack_id, name: r.name, coverThumbUrl: r.cover_thumb_url, sortKey: r.subscribed_at }
}

export function packToCard(p: Pack): PackCard {
  return { id: p.id, name: p.name, coverThumbUrl: p.cover_thumb_url ?? null, sortKey: p.created_at }
}

export function useMySubscriptions() {
  return useQuery({
    queryKey: ['pack', 'subscriptions'] as const,
    queryFn: async () => {
      const sb = getSupabase()
      // 订阅收口走 v_pack_subscription_mine(security definer:订阅永久可看,
      // 已删除/下架包仍出现在列表 — 2026-06-12 owner 决策,无失效标记)
      const { data, error } = await sb
        .from('v_pack_subscription_mine')
        .select('pack_id, subscribed_at, name, description, price_coin, status, integrator_id, cover_thumb_url')
        .order('subscribed_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as SubscriptionRow[]
    },
  })
}

// ---- 未订阅 items 预览(MaAa1/BJddk;替代未订阅时打 items 撞 401) ----

export type PreviewItem = {
  item_id?: string
  name: string
  type: string
  size_original: number | null
  mime_type: string | null
  position: number
  cover_thumb_url?: string | null
}

export async function fetchItemsPreview(
  packId: string,
  opts?: { root?: string; path?: string },
): Promise<PreviewItem[]> {
  const params = new URLSearchParams()
  if (opts?.root) params.set('root', opts.root)
  if (opts?.path) params.set('path', opts.path)
  const qs = params.toString()
  const res = await apiClient.get<{ items: PreviewItem[] }>(
    `/gapi/public/share/${encodeURIComponent(packId)}/items-preview${qs ? `?${qs}` : ''}`,
  )
  return res.items
}
